import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft } from "@phosphor-icons/react";

const BUSINESS_TYPES = [
  "Retail Store",
  "Restaurant",
  "Wholesale",
  "Manufacturing",
  "Service",
  "Healthcare",
  "Technology",
  "Other",
];

export default function EditCustomerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    company_name: "",
    business_type: "",
    notes: "",
  });

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}`);
      const customer = response.data;
      setFormData({
        name: customer.name || "",
        phone: customer.phone || "",
        email: customer.email || "",
        address: customer.address || "",
        company_name: customer.company_name || "",
        business_type: customer.business_type || "",
        notes: customer.notes || "",
      });
    } catch (error) {
      toast.error("Customer not found");
      navigate("/customers");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBusinessTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, business_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Customer name is required");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/customers/${id}`, formData);
      toast.success("Customer updated successfully");
      navigate(`/customers/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update customer");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="edit-customer-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="edit-customer-page">
      {/* Header */}
      <div className="top-bar">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/customers/${id}`)}
            className="p-2 -ml-2 text-[#4B5563] hover:text-[#0A0A0A]"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <p className="overline">EDIT</p>
            <h1 className="top-bar-title">Edit Customer</h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        <div className="card-flat p-4 space-y-4">
          <div>
            <Label htmlFor="company_name" className="text-[#0A0A0A] font-medium">
              Company/Shop Name *
            </Label>
            <Input
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              placeholder="Enter company or shop name"
              className="mt-1.5 h-12 border-[#E5E7EB]"
              data-testid="company-name-input"
            />
          </div>

          <div>
            <Label htmlFor="name" className="text-[#0A0A0A] font-medium">
              Contact Person Name *
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter contact person name"
              className="mt-1.5 h-12 border-[#E5E7EB]"
              data-testid="customer-name-input"
            />
          </div>

          <div>
            <Label htmlFor="business_type" className="text-[#0A0A0A] font-medium">
              Business Type
            </Label>
            <Select
              value={formData.business_type}
              onValueChange={handleBusinessTypeChange}
            >
              <SelectTrigger className="mt-1.5 h-12 border-[#E5E7EB]" data-testid="business-type-select">
                <SelectValue placeholder="Select business type" />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="phone" className="text-[#0A0A0A] font-medium">
              Phone Number
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter phone number"
              className="mt-1.5 h-12 border-[#E5E7EB]"
              data-testid="phone-input"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-[#0A0A0A] font-medium">
              Email Address
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
              className="mt-1.5 h-12 border-[#E5E7EB]"
              data-testid="email-input"
            />
          </div>

          <div>
            <Label htmlFor="address" className="text-[#0A0A0A] font-medium">
              Address
            </Label>
            <Textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter full address"
              rows={2}
              className="mt-1.5 border-[#E5E7EB] resize-none"
              data-testid="address-input"
            />
          </div>

          <div>
            <Label htmlFor="notes" className="text-[#0A0A0A] font-medium">
              Notes
            </Label>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any notes about this customer"
              rows={3}
              className="mt-1.5 border-[#E5E7EB] resize-none"
              data-testid="notes-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full"
          data-testid="update-customer-button"
        >
          {loading ? "Updating..." : "Update Customer"}
        </Button>
      </form>
    </div>
  );
}
